import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { mergeRefs, useUniqueId } from '@ds/react-utils'
import { PopoverManager } from '../../internal/components/PopoverManager'
import type { TooltipProps } from '../Tooltip'

export interface TooltipManagerProps {
  /**
   * Takes a child that will be the element a Tooltip is anchored to.
   *
   * NOTE: All children must accept a `forwardedRef` prop in order to work properly.
   */
  children: React.ReactElement
  /**
   * The Tooltip element.
   */
  tooltip: React.ReactElement<TooltipProps>
  /**
   * An override for the tooltip's visibility
   */
  visible?: boolean
}

/**
 * The TooltipManager component is the recommended implementation for Tooltips and provides the following accessibility requirements:
 *
 * - Show/hide the tooltip on mouseenter/mouseleave
 * - Show/hide the tooltip on keyboard focus/blur
 * - Hide the tooltip when escape is pressed
 * - Keep tooltip visible when mouse enters its content
 *
 * If you would like more fine-tuned control, you can use the `Tooltip`
 * component directly without the `TooltipManager` but you will need to bind the above DOM events to the `Tooltip` to meet accessibility requirements.
 *
 * Please note the `children` prop description with regards to the `forwardedRef` requirement.
 */
export function TooltipManager({
  children,
  tooltip,
  ...restProps
}: TooltipManagerProps) {
  const tooltipId = useUniqueId('short')
  const childRef = useRef<HTMLElement>(null)
  const childElement = React.Children.only(children)

  const childElementAria = childElement.props['aria-describedby']

  const tooltipText = tooltip.props.text
  const childAccessibleName =
    childElement.props.accessibilityText ?? childElement.props.text

  /**
   * This effect is needed to prevent trying to set an attribute when the
   * tooltip is not in the DOM.
   */
  useEffect(() => {
    if (tooltipText !== childAccessibleName) {
      childRef.current?.setAttribute(
        'aria-describedby',
        childElementAria ? `${childElementAria} ${tooltipId}` : tooltipId,
      )
    }
  }, [childAccessibleName, childElementAria, tooltipId, tooltipText])

  // Consumer is overriding visibility, turn off PopoverManager events
  const enableEvents = typeof restProps.visible !== 'boolean'

  return (
    <PopoverManager
      dismissOnEscape={enableEvents}
      interactive={enableEvents}
      showOnFocus={enableEvents}
      showOnMouseEnter={enableEvents}
      popover={({ popoverRef, triggerElement, visible }) =>
        React.cloneElement(tooltip, {
          anchorElement: triggerElement,
          forwardedRef: mergeRefs(
            popoverRef || undefined,
            tooltip.props.forwardedRef || undefined,
          ),
          visible: restProps.visible ?? visible,
          id: tooltipId,
        })
      }
      trigger={({ triggerRef }) =>
        React.cloneElement(childElement, {
          /**
           * Assumes that the trigger has forwardedRef prop.
           * (i.e. does not work with <span>)
           */
          forwardedRef: mergeRefs(
            childElement.props.forwardedRef,
            childRef,
            triggerRef,
          ),
        })
      }
    />
  )
}

TooltipManager.propTypes = {
  children: PropTypes.element.isRequired,
  tooltip: PropTypes.element.isRequired,
  visible: PropTypes.bool,
}

TooltipManager.displayName = 'TooltipManager'
